import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { API_BASE_URL } from "../../api/client";
import styles from "./Chatbot.module.css";

/** Admin-managed widget config served by GET /api/chatbot/config. */
type ChatConfig = {
  enabled: boolean;
  botName: string;
  greeting: string;
  quickPrompts: { emoji: string; label: string; prompt: string }[];
  capabilityPhrases: string[];
};

/**
 * Creates a pleasant notification chime using Web Audio API
 */
function createNotificationSound(): () => void {
  let audioContext: AudioContext | null = null;

  return () => {
    try {
      if (!audioContext) {
        audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      }
      const now = audioContext.currentTime;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.setValueAtTime(659.25, now);
      oscillator.frequency.setValueAtTime(783.99, now + 0.1);
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.3, now + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      oscillator.start(now);
      oscillator.stop(now + 0.3);
    } catch {
      // ignore
    }
  };
}

type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

function formatMessageContent(content: string): React.ReactNode {
  const lines = content.split("\n");

  return lines.map((line, lineIndex) => {
    const combinedPattern =
      /(https?:\/\/[^\s]+)|(\*\*([^*]+)\*\*)|((?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4})/g;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = combinedPattern.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }

      const [fullMatch, url, boldFull, boldText, phone] = match;

      if (url) {
        parts.push(
          <a
            key={`${lineIndex}-${match.index}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            {url}
          </a>
        );
      } else if (boldFull && boldText) {
        parts.push(<strong key={`${lineIndex}-${match.index}`}>{boldText}</strong>);
      } else if (phone) {
        const cleanPhone = phone.replace(/[^\d+]/g, "");
        parts.push(
          <a
            key={`${lineIndex}-${match.index}`}
            href={`tel:${cleanPhone}`}
            className={styles.phoneLink}
          >
            {phone}
          </a>
        );
      }

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }
    if (parts.length === 0) {
      parts.push(line);
    }

    return (
      <span key={lineIndex}>
        {parts}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    );
  });
}

const CHAT_ENDPOINT = process.env.REACT_APP_CHATBOT_URL
  ? `${process.env.REACT_APP_CHATBOT_URL}/chat`
  : "/api/chat";

const SYSTEM_PROMPT =
  "You are an upbeat assistant for Playfunia indoor playgrounds. Provide concise, friendly answers and suggest next steps when helpful.";

const DEFAULT_GREETING =
  "Hi! I'm the Playfunia helper. Ask me about prices, hours, parties, memberships — or pick a question below to get started 👇";

// Phrases that cycle next to the floating button — short capability teasers.
// Defaults only; the live values come from the admin-managed chat config.
const DEFAULT_CAPABILITY_PHRASES = [
  "Plan a birthday party 🎂",
  "Check today's hours ⏰",
  "Find ticket prices 🎟️",
  "Compare memberships 💳",
  "See current deals 🎁",
  "Ask me anything ✨",
];

// Quick-start chips shown on first open. Clicking sends the prompt directly.
// Defaults only; the live values come from the admin-managed chat config.
const DEFAULT_QUICK_PROMPTS: { emoji: string; label: string; prompt: string }[] = [
  { emoji: "🎂", label: "Birthday party for 10 kids?",     prompt: "How much for a birthday party for 10 kids?" },
  { emoji: "⏰", label: "Are you open right now?",         prompt: "Are you open right now?" },
  { emoji: "🎟️", label: "Ticket prices",                   prompt: "What are your open play ticket prices?" },
  { emoji: "💳", label: "Membership plans",                prompt: "Tell me about your membership plans" },
  { emoji: "🎁", label: "Current deals & promos",          prompt: "What promotions are running right now?" },
  { emoji: "❓", label: "Do we need a waiver?",             prompt: "Do we need to sign a waiver?" },
];

// SVG icons — inline so no extra deps and we can animate fill/stroke.
// Refined speech-bubble with a soft, friendly face — clean rounded silhouette
// that reads clearly at small sizes and feels polished/professional.
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" aria-hidden="true">
    <path
      d="M12 3C6.99 3 3 6.4 3 10.6c0 2.05.97 3.9 2.55 5.27-.1 1.2-.58 2.3-1.4 3.18-.3.32-.08.85.36.81 1.8-.16 3.34-.78 4.5-1.66 1.02.31 2.12.48 3.12.48 5.01 0 9-3.4 9-7.6S17.01 3 12 3Z"
      fill="currentColor"
    />
    {/* friendly eyes + smile */}
    <circle cx="8.6" cy="10.3" r="1.15" fill="#fff" />
    <circle cx="15.4" cy="10.3" r="1.15" fill="#fff" />
    <path
      d="M8.6 13c.85.95 2.05 1.45 3.4 1.45S14.55 13.95 15.4 13"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <path
      d="M4 9V5a1 1 0 0 1 1-1h4M20 9V5a1 1 0 0 0-1-1h-4M4 15v4a1 1 0 0 0 1 1h4M20 15v4a1 1 0 0 1-1 1h-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CollapseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <path
      d="M9 4v4a1 1 0 0 1-1 1H4M15 4v4a1 1 0 0 0 1 1h4M9 20v-4a1 1 0 0 0-1-1H4M15 20v-4a1 1 0 0 1 1-1h4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <path
      d="M3 11.5 21 3l-8.5 18-2-7-7.5-2.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      strokeLinecap="round"
      fill="rgba(255,255,255,0.15)"
    />
  </svg>
);

export function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [config, setConfig] = useState<ChatConfig | null>(null);
  const [configDisabled, setConfigDisabled] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: "welcome", role: "assistant", content: DEFAULT_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const playNotificationRef = useRef<(() => void) | null>(null);

  // Has the user sent at least one message? Controls whether quick chips show.
  const hasUserChatted = useMemo(
    () => messages.some(m => m.role === "user"),
    [messages]
  );

  useEffect(() => {
    playNotificationRef.current = createNotificationSound();
  }, []);

  // Load admin-managed config (greeting, quick prompts, launcher phrases, on/off).
  // Falls back silently to the built-in defaults if the request fails.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/chatbot/config`)
      .then(res => (res.ok ? (res.json() as Promise<Partial<ChatConfig>>) : Promise.reject(new Error("config unavailable"))))
      .then(data => {
        if (cancelled || !data || typeof data !== "object") return;
        if (data.enabled === false) {
          setConfigDisabled(true);
          return;
        }
        const next: ChatConfig = {
          enabled: true,
          botName: typeof data.botName === "string" && data.botName ? data.botName : "Playfunia Assistant",
          greeting: typeof data.greeting === "string" && data.greeting.trim() ? data.greeting : DEFAULT_GREETING,
          quickPrompts: Array.isArray(data.quickPrompts)
            ? data.quickPrompts.filter(q => q && typeof q.label === "string" && typeof q.prompt === "string")
            : DEFAULT_QUICK_PROMPTS,
          capabilityPhrases: Array.isArray(data.capabilityPhrases) && data.capabilityPhrases.length
            ? data.capabilityPhrases.filter(p => typeof p === "string" && p.trim())
            : DEFAULT_CAPABILITY_PHRASES,
        };
        setConfig(next);
        // Swap the greeting only while the conversation hasn't started.
        setMessages(prev =>
          prev.length === 1 && prev[0].id === "welcome"
            ? [{ id: "welcome", role: "assistant", content: next.greeting }]
            : prev
        );
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  const quickPrompts = config?.quickPrompts ?? DEFAULT_QUICK_PROMPTS;
  const capabilityPhrases = config?.capabilityPhrases ?? DEFAULT_CAPABILITY_PHRASES;
  const phraseCount = capabilityPhrases.length;

  // Cycle the capability phrase while the bot is closed.
  useEffect(() => {
    if (isOpen) return;
    if (phraseCount === 0) return;
    const interval = window.setInterval(() => {
      setPhraseIndex(i => (i + 1) % phraseCount);
    }, 2800);
    return () => window.clearInterval(interval);
  }, [isOpen, phraseCount]);

  // Lock body scroll while in fullscreen, restore when leaving.
  useEffect(() => {
    if (!(isOpen && isFullscreen)) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen, isFullscreen]);

  // Auto-scroll to newest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isSending]);

  // Cleanup any in-flight request on unmount.
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    playNotificationRef.current?.();
  }, []);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value);
    const textarea = event.target;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 140)}px`;
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      if (prev && abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Exit fullscreen when closing.
      if (prev) setIsFullscreen(false);
      return !prev;
    });
  }, []);

  const payloadMessages = useMemo(() => {
    const history = messages.map(message => ({
      role: message.role,
      content: message.content,
    }));
    return [{ role: "system", content: SYSTEM_PROMPT }, ...history];
  }, [messages]);

  const sendPrompt = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
      };
      setMessages(prev => [...prev, userMessage]);
      setInput("");
      setError(null);
      if (textareaRef.current) textareaRef.current.style.height = "auto";

      setIsSending(true);
      fetch(CHAT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [...payloadMessages, { role: "user", content: trimmed }],
        }),
        signal: controller.signal,
      })
        .then(async response => {
          if (!response.ok) {
            const detail = await response.text();
            throw new Error(detail || "The assistant is unavailable right now.");
          }
          return response.json() as Promise<{ reply: string }>;
        })
        .then(data => {
          setMessages(prev => [
            ...prev,
            {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              content: data.reply || "I didn't receive a response. Could you try asking again?",
            },
          ]);
          playNotificationSound();
        })
        .catch(errorInstance => {
          if (
            controller.signal.aborted ||
            (errorInstance instanceof Error && errorInstance.name === "AbortError")
          ) {
            return;
          }
          setError(errorInstance instanceof Error ? errorInstance.message : "Something went wrong.");
        })
        .finally(() => {
          if (abortControllerRef.current === controller) {
            setIsSending(false);
            abortControllerRef.current = null;
          }
        });
    },
    [payloadMessages, playNotificationSound]
  );

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendPrompt(input);
  };

  const panelClass = [
    styles.panel,
    isFullscreen ? styles.panelFullscreen : "",
  ].filter(Boolean).join(" ");

  const currentPhrase = phraseCount > 0 ? capabilityPhrases[phraseIndex % phraseCount] : "";

  // Admins can switch the assistant off; render nothing at all in that case.
  if (configDisabled) return null;

  return (
    <div className={styles.widget} aria-live="polite">
      {isOpen ? (
        <>
          {isFullscreen ? <div className={styles.backdrop} onClick={() => setIsFullscreen(false)} /> : null}
          <div className={panelClass} role="dialog" aria-label="Playfunia chatbot">
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <div className={styles.avatar}>
                  <ChatIcon />
                  <span className={styles.statusDot} aria-hidden="true" />
                </div>
                <div className={styles.headerText}>
                  <h3>Playfunia Helper</h3>
                  <span className={styles.statusText}>Online · usually replies in seconds</span>
                </div>
              </div>
              <div className={styles.headerActions}>
                <button
                  className={`${styles.iconButton} ${styles.hideOnMobile}`}
                  onClick={() => setIsFullscreen(f => !f)}
                  aria-label={isFullscreen ? "Collapse to compact" : "Expand to full screen"}
                  title={isFullscreen ? "Collapse" : "Expand"}
                >
                  {isFullscreen ? <CollapseIcon /> : <ExpandIcon />}
                </button>
                <button
                  className={styles.iconButton}
                  onClick={toggle}
                  aria-label="Close chat"
                  title="Close"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className={styles.messages}>
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`${styles.message} ${message.role === "user" ? styles.messageUser : styles.messageBot}`}
                >
                  {formatMessageContent(message.content)}
                </div>
              ))}

              {!hasUserChatted ? (
                <div className={styles.quickPrompts}>
                  {quickPrompts.map((q, i) => (
                    <button
                      key={`${q.label}-${i}`}
                      type="button"
                      className={styles.quickChip}
                      style={{ animationDelay: `${0.1 + i * 0.06}s` }}
                      onClick={() => sendPrompt(q.prompt)}
                      disabled={isSending}
                    >
                      <span className={styles.chipEmoji}>{q.emoji}</span>
                      <span>{q.label}</span>
                    </button>
                  ))}
                </div>
              ) : null}

              {isSending ? (
                <div className={`${styles.message} ${styles.messageBot} ${styles.typing}`}>
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                </div>
              ) : null}

              <div ref={messagesEndRef} />
            </div>

            <form className={styles.composer} onSubmit={handleSubmit}>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={event => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    if (input.trim() && !isSending) {
                      handleSubmit(event as unknown as React.FormEvent<HTMLFormElement>);
                    }
                  }
                }}
                placeholder="Ask anything about Playfunia..."
                rows={1}
                maxLength={2000}
                disabled={isSending}
              />
              <button
                type="submit"
                disabled={isSending || !input.trim()}
                aria-label="Send message"
              >
                <SendIcon />
              </button>
            </form>

            {error ? <div className={styles.status}>{error}</div> : null}

            <div className={styles.poweredBy}>
              powered by{' '}
              <a
                className={styles.poweredByBrand}
                href="https://callsphere.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                CallSphere LLC
              </a>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.launcher}>
          {currentPhrase ? (
            <button
              className={styles.tooltipBubble}
              onClick={toggle}
              aria-label={`Open chat. Tip: ${currentPhrase}`}
            >
              <span key={phraseIndex} className={styles.tooltipPhrase}>
                {currentPhrase}
              </span>
              <span className={styles.tooltipTail} aria-hidden="true" />
            </button>
          ) : null}
          <button
            className={styles.fab}
            onClick={toggle}
            aria-label="Open Playfunia chatbot"
          >
            <span className={styles.fabAura} aria-hidden="true" />
            <span className={styles.fabRing} aria-hidden="true" />
            <span className={styles.fabIcon}>
              <ChatIcon />
            </span>
            <span className={styles.fabSparkle} aria-hidden="true">✨</span>
            <span className={styles.fabStatus} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
